import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, of, catchError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Permission, CreatePermissionRequest, UpdatePermissionRequest, PermissionCheckResponse, UserPermissionsResponse, userPermissionsResponseToFlat } from '../models/permission.model';
import { AuthService } from './auth.service';
import { UserService, UsersResponse } from './user.service';

export interface RindegastosUser {
    id: number;
    email: string;
}

export interface RindegastosUsersPage {
    data: RindegastosUser[];
    total: number;
    page: number;
    totalPages: number;
}

/**
 * TEMPORARY (product owner): usuarios que todavía no tienen permisos asignados
 * pueden ver, crear y editar todos los módulos excepto soporte, integraciones
 * e infraestructura. No incluye acciones destructivas (eliminar, borrado masivo,
 * suspender o desactivar). Quitar cuando el backend asigne permisos por defecto.
 */
export const TEMP_DEFAULT_ACCESS_FOR_USERS_WITHOUT_PERMISSIONS = true;

/** Módulos (y alias de menú/ruta) que el fallback temporal no abre. */
const TEMP_EXCLUDED_MODULES = new Set([
    'support-tickets',
    'support',
    'soporte',
    'tickets',
    'integrations',
    'integraciones',
    'integrations-catalog',
    'infra',
    'infraestructura',
    'infrastructure',
    // Editar factura completa: sólo para usuarios con el módulo asignado a mano.
    'bill-edit',
]);

/**
 * Integraciones e infraestructura heredan `admin` solo si no tienen fila propia.
 * Una fila explícita (`integrations: read`) no se eleva por tener `admin: write`.
 */
const ADMIN_ALIAS_MODULES = new Set([
    'integrations',
    'integraciones',
    'integrations-catalog',
    'infra',
    'infraestructura',
    'infrastructure',
]);

@Injectable({
    providedIn: 'root'
})
export class PermissionsService {
    private readonly PERMISSIONS_CACHE_KEY = 'bdl_permissions_cache';
    private readonly PERMISSIONS_CACHE_USER_KEY = 'bdl_permissions_cache_user';
    private permissionsSubject = new BehaviorSubject<Permission[]>([]);
    public permissions$ = this.permissionsSubject.asObservable();
    private permissionsLoaded = false;
    /**
     * These GETs run in the same turn as login. A 401/403 here must not clear
     * the token that was just stored, or the shell swap races a logout and
     * the screen stays blank.
     */
    private readonly sessionSafeHeaders = { 'X-Skip-Logout': '1' };

    constructor(
        private http: HttpClient,
        private injector: Injector,
        private userService: UserService
    ) {
        this.loadPermissionsFromCache();
    }

    /** Lazy resolve avoids NG0200: PermissionsService ↔ AuthService (e.g. Router/bootstrap chain). */
    private auth(): AuthService {
        return this.injector.get(AuthService);
    }

    private loadPermissionsFromCache(): void {
        const cached = localStorage.getItem(this.PERMISSIONS_CACHE_KEY);
        const cachedUser = localStorage.getItem(this.PERMISSIONS_CACHE_USER_KEY);
        const currentUser = this.auth().getCurrentUser();
        const userIdentifier = this.getCurrentUserEmail() || currentUser?.id?.toString();
        
        if (cached && cachedUser && currentUser && userIdentifier) {
            // Validate that cached permissions belong to current user
            if (cachedUser === userIdentifier) {
                try {
                    const permissions = JSON.parse(cached);
                    this.permissionsSubject.next(permissions);
                    this.permissionsLoaded = true;
                } catch (e) {
                    console.error('Error loading permissions from cache:', e);
                    this.clearCache();
                }
            } else {
                // Cache belongs to different user, clear it
                this.clearCache();
            }
        } else if (cached && !currentUser) {
            // No current user but cache exists, clear it
            this.clearCache();
        }
    }

    private savePermissionsToCache(permissions: Permission[]): void {
        const userIdentifier = this.getCurrentUserEmail() || this.auth().getCurrentUser()?.id?.toString();
        if (userIdentifier) {
            localStorage.setItem(this.PERMISSIONS_CACHE_KEY, JSON.stringify(permissions));
            localStorage.setItem(this.PERMISSIONS_CACHE_USER_KEY, userIdentifier);
        }
    }

    clearCache(): void {
        localStorage.removeItem(this.PERMISSIONS_CACHE_KEY);
        localStorage.removeItem(this.PERMISSIONS_CACHE_USER_KEY);
        this.permissionsSubject.next([]);
        this.permissionsLoaded = false;
    }

    loadUserPermissions(): Observable<Permission[]> {
        const user = this.auth().getCurrentUser();
        if (!user) {
            return of([]);
        }

        return this.http.get<UserPermissionsResponse>(`${environment.apiUrl}/permissions/user/${user.id}`, {
            headers: this.sessionSafeHeaders,
        })
            .pipe(
                tap(response => {
                    const permissions = userPermissionsResponseToFlat(response);
                    this.permissionsSubject.next(permissions);
                    this.savePermissionsToCache(permissions);
                    this.permissionsLoaded = true;
                }),
                map(response => userPermissionsResponseToFlat(response)),
                catchError(error => {
                    console.error('Error loading user permissions:', error);
                    return of([]);
                })
            );
    }

    loadUserPermissionsByEmail(): Observable<Permission[]> {
        const email = this.getCurrentUserEmail();
        if (!email) {
            return of([]);
        }

        return this.http.get<UserPermissionsResponse>(`${environment.apiUrl}/permissions/email/${encodeURIComponent(email)}`, {
            headers: this.sessionSafeHeaders,
        })
            .pipe(
                tap(response => {
                    const permissions = userPermissionsResponseToFlat(response);
                    this.permissionsSubject.next(permissions);
                    this.savePermissionsToCache(permissions);
                    this.permissionsLoaded = true;
                }),
                map(response => userPermissionsResponseToFlat(response)),
                catchError(error => {
                    console.error('Error loading user permissions by email:', error);
                    return of([]);
                })
            );
    }

    getAll(): Observable<Permission[]> {
        return this.http.get<Permission[]>(`${environment.apiUrl}/permissions`);
    }

    /** Returns one page of @rindegastos.com users for the permissions management list. */
    getRindegastosUsersPage(
        page = 1,
        limit = 100,
        emailFilter?: string
    ): Observable<RindegastosUsersPage> {
        const trimmed = emailFilter?.trim();
        const listParams: {
            page: number;
            limit: number;
            username: string;
            usernameMatch: 'contains' | 'endsWith';
            usernameContains?: string;
        } = {
            page,
            limit,
            // Always scope to @rindegastos.com; optional text is an extra AND contains filter
            username: '@rindegastos.com',
            usernameMatch: 'endsWith',
            ...(trimmed ? { usernameContains: trimmed } : {}),
        };

        const asPaged = (res: unknown): UsersResponse =>
            Array.isArray(res)
                ? { data: res, page: 1, totalPages: 1, total: res.length }
                : (res as UsersResponse);

        return this.userService.getUsers(listParams).pipe(
            map((res) => {
                const r = asPaged(res);
                return {
                    data: (r.data || []).map((u) => ({ id: u.id, email: u.username })),
                    total: r.total ?? 0,
                    page: r.page ?? page,
                    totalPages: r.totalPages ?? 1,
                };
            }),
            catchError((error) => {
                console.error('Error loading rindegastos users:', error);
                return of({ data: [], total: 0, page: 1, totalPages: 0 });
            })
        );
    }

    getByUserId(userId: number): Observable<Permission[]> {
        return this.http.get<UserPermissionsResponse>(`${environment.apiUrl}/permissions/user/${userId}`).pipe(
            map(response => userPermissionsResponseToFlat(response)),
            catchError(error => {
                console.error('Error loading permissions for user:', error);
                return of([]);
            })
        );
    }

    getUserPermissionsDoc(userId: number): Observable<UserPermissionsResponse | null> {
        return this.http.get<UserPermissionsResponse | null>(`${environment.apiUrl}/permissions/user/${userId}`);
    }

    removeUserModule(userId: number, module: string): Observable<void> {
        return this.http.delete<void>(`${environment.apiUrl}/permissions/user/${userId}/module/${encodeURIComponent(module)}`);
    }

    getByUserEmail(userEmail: string): Observable<Permission[]> {
        return this.http.get<Permission[]>(`${environment.apiUrl}/permissions/email/${encodeURIComponent(userEmail)}`);
    }

    getByModule(module: string): Observable<UserPermissionsResponse[]> {
        return this.http.get<UserPermissionsResponse[]>(`${environment.apiUrl}/permissions/module/${module}`);
    }

    getAvailableModules(): Observable<string[]> {
        return this.http.get<string[]>(`${environment.apiUrl}/permissions/modules`);
    }

    checkPermission(userId: number, module: string): Observable<PermissionCheckResponse> {
        return this.http.get<PermissionCheckResponse>(`${environment.apiUrl}/permissions/check/${userId}/${module}`);
    }

    checkPermissionByEmail(userEmail: string, module: string): Observable<PermissionCheckResponse> {
        return this.http.get<PermissionCheckResponse>(`${environment.apiUrl}/permissions/check-email/${encodeURIComponent(userEmail)}/${module}`);
    }

    create(permission: CreatePermissionRequest): Observable<Permission> {
        return this.http.post<Permission>(`${environment.apiUrl}/permissions`, permission);
    }

    update(id: string, permission: UpdatePermissionRequest): Observable<Permission> {
        return this.http.put<Permission>(`${environment.apiUrl}/permissions/${id}`, permission);
    }

    delete(id: string): Observable<void> {
        return this.http.delete<void>(`${environment.apiUrl}/permissions/${id}`);
    }

    /**
     * Loads the current user's permissions once. A failed request does not mark
     * them as loaded, so the temporary fallback does not apply on a network error.
     */
    ensureCurrentUserPermissionsLoaded(): Observable<Permission[]> {
        if (this.permissionsLoaded) {
            return of(this.getCurrentUserPermissions());
        }
        const email = this.getCurrentUserEmail();
        if (email?.endsWith('@rindegastos.com')) {
            return this.loadUserPermissionsByEmail();
        }
        if (this.auth().getCurrentUser()?.id) {
            return this.loadUserPermissions();
        }
        return of([]);
    }

    /** True only after a successful load that returned no permission rows. */
    isTemporaryDefaultAccessActive(): boolean {
        return TEMP_DEFAULT_ACCESS_FOR_USERS_WITHOUT_PERMISSIONS
            && this.permissionsLoaded
            && this.permissionsSubject.value.length === 0;
    }

    isExcludedFromTemporaryDefault(module: string): boolean {
        return TEMP_EXCLUDED_MODULES.has((module || '').trim().toLowerCase());
    }

    /** Route/menu gate: block soporte, integraciones and infraestructura for the fallback only. */
    isBlockedByTemporaryDefault(module: string): boolean {
        return this.isTemporaryDefaultAccessActive() && this.isExcludedFromTemporaryDefault(module);
    }

    /**
     * Delete, suspend and deactivate outside bulk-upload (see destructive-action interceptor
     * allowlist for /bulk-upload/* and /workflow-conditions/*).
     * The temporary fallback never grants this elsewhere. Users who already have any
     * permission keep the previous behavior (these actions were not permission-scoped).
     * Before permissions load, stays allowed so users with permissions do not flash-hide.
     */
    canPerformDestructiveAction(): boolean {
        if (!TEMP_DEFAULT_ACCESS_FOR_USERS_WITHOUT_PERMISSIONS) {
            return true;
        }
        if (!this.permissionsLoaded) {
            return true;
        }
        return this.permissionsSubject.value.length > 0;
    }

    hasPermission(module: string): boolean {
        return this.hasReadPermission(module);
    }

    hasWritePermission(module: string): boolean {
        if (this.hasRealAccess(module, true)) {
            return true;
        }
        // Fallback write is create/edit only. Destructive actions use canPerformDestructiveAction().
        return this.allowsTemporaryDefault(module);
    }

    hasReadPermission(module: string): boolean {
        if (this.hasRealAccess(module, false)) {
            return true;
        }
        return this.allowsTemporaryDefault(module);
    }

    getPermissionLevel(module: string): 'read' | 'write' | null {
        const direct = this.permissionsSubject.value.find(p => p.module === module);
        if (direct) {
            return direct.level;
        }
        if (this.isAdminAliasModule(module)) {
            const admin = this.permissionsSubject.value.find(p => p.module === 'admin');
            if (admin) {
                return admin.level;
            }
        }
        if (this.allowsTemporaryDefault(module)) {
            return 'write';
        }
        return null;
    }

    private allowsTemporaryDefault(module: string): boolean {
        return this.isTemporaryDefaultAccessActive() && !this.isExcludedFromTemporaryDefault(module);
    }

    private hasRealAccess(module: string, writeOnly: boolean): boolean {
        if (this.permissionsSubject.value.some(p => p.module === module)) {
            return this.matchesModule(module, writeOnly);
        }
        return this.isAdminAliasModule(module) && this.matchesModule('admin', writeOnly);
    }

    private matchesModule(module: string, writeOnly: boolean): boolean {
        return this.permissionsSubject.value.some(p => {
            if (p.module !== module) {
                return false;
            }
            return writeOnly ? p.level === 'write' : (p.level === 'read' || p.level === 'write');
        });
    }

    private isAdminAliasModule(module: string): boolean {
        return ADMIN_ALIAS_MODULES.has((module || '').trim().toLowerCase());
    }

    isRindegastosUser(): boolean {
        const user = this.auth().getCurrentUser();
        const email = user?.username || (user as { email?: string })?.email || '';
        if (!user || !email) {
            return false;
        }
        return email.endsWith('@rindegastos.com');
    }

    /** User email/username for permission lookups (handles both API shapes). */
    getCurrentUserEmail(): string | null {
        const user = this.auth().getCurrentUser();
        return (user?.username || (user as { email?: string })?.email || null) ?? null;
    }

    getCurrentUserPermissions(): Permission[] {
        return this.permissionsSubject.value;
    }

    arePermissionsLoaded(): boolean {
        return this.permissionsLoaded;
    }
}
